/**
 * Table widget totals row (#4409) — aggregates the metrics already fetched
 * for the widget's currently displayed rows.
 *
 * Additive metrics (impressions, spend, clicks, …) sum cleanly. A ratio
 * metric (CTR, CPA, …) does NOT — averaging per-row percentages weights every
 * row equally regardless of its volume, which is exactly the "naive summing"
 * the ticket calls out. The correct total is numerator-sum / denominator-sum,
 * which only works when both underlying metrics are ALSO columns on this
 * table; otherwise there is nothing here to derive it from, and the cell is
 * left blank rather than showing a wrong number.
 */
import { CONVERSION_METRIC_KEYS, REACH_METRIC_KEYS, getMetricDef } from './canonicalMetrics';

interface RatioComponents {
  numerator: string;
  denominator: string;
  /** e.g. CPM = (spend / impressions) * 1000. */
  multiplier?: number;
}

const RATIO_METRIC_COMPONENTS: Record<string, RatioComponents> = {
  ctr: { numerator: 'clicks', denominator: 'impressions' },
  cpcv: { numerator: 'spend', denominator: 'video_completes' },
};

/**
 * Reach, Frequency, Conversions, CPA and ROAS can't be totalled from the
 * displayed rows at all, additive or derived (flank#5051 production retest):
 * each DSP reports its own audience, so adding (or dividing) reach-derived
 * values across rows double-counts anyone reached on more than one — the
 * same failure `REACH_METRIC_KEYS`/`CONVERSION_METRIC_KEYS` already document
 * for per-row visibility (#5052). `frequency`/`cpa` used to have ratio
 * formulas here, which computed a confidently wrong number instead of
 * leaving the cell blank.
 *
 * Used by `maskNeverTotalValues` below for the "All <dimension>" row
 * (DashboardBuilder.tsx): that row reads its own single-row, ungrouped
 * aggregate fetch directly rather than going through `computeTotalsRow`, so
 * it needs the same mask applied to whatever the API returns for these keys
 * (flank#5051 production retest: the API answered Reach 0 / Frequency 0.00
 * for that ungrouped multi-platform query, which rendered as a real number
 * instead of "—").
 */
export const NEVER_TOTAL_KEYS = new Set([...REACH_METRIC_KEYS, ...CONVERSION_METRIC_KEYS]);

/**
 * Applies the `NEVER_TOTAL_KEYS` mask to an already-fetched row of values, in
 * `metricKeys` order — the "All <dimension>" row's one-line transformation,
 * pulled out so it's unit-testable without rendering `DashboardBuilder` or
 * mocking its report fetches (CodeRabbit, flank#5051: a future edit reverting
 * to reading `allRow` values directly would otherwise pass unnoticed).
 */
export function maskNeverTotalValues(
  metricKeys: string[],
  readValue: (key: string) => number | null,
): Array<number | null> {
  return metricKeys.map(key => (NEVER_TOTAL_KEYS.has(key) ? null : readValue(key)));
}

/**
 * CPM and CPC are fetched directly as the API's own ratio metrics, not
 * derived from spend/impressions or spend/clicks (canonicalMetrics.ts) — the
 * API's denominator excludes GAM's structurally-unpriced sell-side rows, so a
 * naive spend-total ÷ clicks-total here would disagree with the same row's
 * own already-correct per-row value, the exact ~40% dilution that's why these
 * two are fetched metrics in the first place rather than derived ones
 * (CodeRabbit, flank#5065). Client CPM has the same shape (client_spend ÷
 * impressions, null-aware) with no raw components to sum at all. All three
 * total as `null` (an em dash) rather than a recomputed number nothing here
 * can verify against the row values already on screen.
 */
const API_ONLY_RATIO_KEYS = new Set(['cpm', 'cpc', 'client_cpm']);

const sum = (rows: Array<Record<string, unknown>>, key: string): number =>
  rows.reduce((total, row) => total + (Number(row[key]) || 0), 0);

/**
 * One total per metric key, in the same order. `null` means "can't be
 * totalled honestly" — a ratio metric whose components aren't both present
 * as columns — and should render as an em dash, not 0.
 */
export function computeTotalsRow(rows: Array<Record<string, unknown>>, metricKeys: string[]): Array<number | null> {
  return metricKeys.map(key => {
    if (NEVER_TOTAL_KEYS.has(key) || API_ONLY_RATIO_KEYS.has(key)) return null;
    const ratio = RATIO_METRIC_COMPONENTS[key];
    if (!ratio) {
      // Additive only for metrics with no ratio formula — a format of
      // 'percent'/'ratio'/'multiple' we don't recognize is safer left blank
      // than silently summed.
      const format = getMetricDef(key)?.format;
      return format === 'count' || format === 'currency' ? sum(rows, key) : null;
    }
    if (!metricKeys.includes(ratio.numerator) || !metricKeys.includes(ratio.denominator)) return null;
    const denominatorTotal = sum(rows, ratio.denominator);
    if (denominatorTotal === 0) return null;
    return (sum(rows, ratio.numerator) / denominatorTotal) * (ratio.multiplier ?? 1);
  });
}
